/**
 * Indicadores de progreso para el espejo Kommo.
 *
 * La API de listado no informa necesariamente un total de leads. Por eso
 * nunca inferimos un porcentaje desde "páginas procesadas": sólo se estima
 * si existe un recorrido completo previo, identificado como cobertura complete.
 *
 * Todos los porcentajes y ETA derivados de ese recorrido son APROXIMADOS:
 * los leads pueden haber cambiado desde entonces.
 */
export function formatSyncDuration(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  const rounded = Math.ceil(seconds);
  if (rounded < 60) return rounded + ' s';
  const mins = Math.floor(rounded / 60);
  const secs = rounded % 60;
  if (mins < 60) return mins + ' min' + (secs ? ' ' + secs + ' s' : '');
  const hours = Math.floor(mins / 60);
  const remMinutes = mins % 60;
  return hours + ' h' + (remMinutes ? ' ' + remMinutes + ' min' : '');
}

function validNonnegative(value) {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/**
 * @param {Object|null} result      Última respuesta del GAS o progreso en checkpoint
 * @param {{
 *   initialLeads?: number,
 *   initialPages?: number,
 *   elapsedSeconds?: number,
 *   previousCompleteTotal?: number|null
 * }} context
 */
export function describeKommoSyncProgress(result, context = {}) {
  if (!result) return null;
  const leads = Math.trunc(validNonnegative(result.filasLeads));
  const pages = Math.trunc(validNonnegative(result.paginas));
  const complete = result.completed === true || result.status === 'complete';
  const initialLeads = Math.trunc(validNonnegative(context.initialLeads));
  const initialPages = Math.trunc(validNonnegative(context.initialPages));
  const elapsedSeconds = validNonnegative(context.elapsedSeconds);
  const newlyProcessed = Math.max(0, leads - initialLeads);
  const newlyProcessedPages = Math.max(0, pages - initialPages);
  const leadsPerMinute = elapsedSeconds > 0 && newlyProcessedPages >= 1
    ? newlyProcessed / elapsedSeconds * 60
    : null;

  const previous = Number(context.previousCompleteTotal);
  const hasReference = context.previousCompleteTotal != null &&
    Number.isFinite(previous) && previous > 0;

  // Nunca afirmar 100% ni mostrar cuenta regresiva 0 si aún quedan páginas.
  // Si ya superamos el total anterior, la estimación deja de ser válida.
  const canEstimate = !complete && hasReference && leads < previous &&
    newlyProcessedPages >= 2 && elapsedSeconds >= 2 &&
    leadsPerMinute != null && leadsPerMinute > 0;
  const estimatedRemainingLeads = canEstimate ? Math.max(0, previous - leads) : null;
  const estimatedRemainingSeconds = canEstimate
    ? Math.ceil(estimatedRemainingLeads / leadsPerMinute * 60)
    : null;
  const estimatedPercent = canEstimate
    ? Math.min(99, Math.max(0, Math.floor(leads / previous * 100)))
    : null;

  return {
    leads,
    pages,
    complete,
    elapsedSeconds,
    leadsPerMinute,
    hasReference,
    previousCompleteTotal: hasReference ? Math.round(previous) : null,
    estimatedPercent,
    estimatedRemainingLeads,
    estimatedRemainingSeconds,
    indeterminate: !complete && estimatedPercent == null
  };
}
